import { useState } from 'react';
import { ConsultationNote } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { Checkbox } from '../../../components/ui/Checkbox';

interface NotesSectionProps {
  notes: ConsultationNote[];
  onAddNote: (content: string, isInternal: boolean) => void;
}

const NotesSection = ({ notes, onAddNote }: NotesSectionProps) => {
  const [newNote, setNewNote] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newNote.trim()) {
      setIsAdding(true);
      setTimeout(() => {
        onAddNote(newNote, isInternal);
        setNewNote('');
        setIsInternal(false);
        setIsAdding(false);
      }, 500);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Anotações</h2>
        <span className="text-sm text-muted-foreground">
          {notes.length} {notes.length === 1 ? 'anotação' : 'anotações'}
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Adicionar nova anotação..."
            className="w-full min-h-[100px] px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            disabled={isAdding}
          />
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            label="Anotação interna (visível apenas para equipe)"
            checked={isInternal}
            onChange={(e) => setIsInternal(e.target.checked)}
            disabled={isAdding}
          />
          <Button
            type="submit"
            variant="default"
            iconName="Plus"
            iconPosition="left"
            loading={isAdding}
            disabled={!newNote.trim() || isAdding}
          >
            Adicionar Anotação
          </Button>
        </div>
      </form>

      <div className="space-y-3">
        {notes.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="MessageSquare" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhuma anotação adicionada</p>
          </div>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className={`p-4 rounded-lg border ${
                note.isInternal
                  ? 'bg-warning/5 border-warning/20' :'bg-muted border-border'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{note.author}</span>
                  {note.isInternal && (
                    <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs font-medium rounded">
                      Interna
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {note.createdAt.toLocaleString('pt-BR')}
                </span>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                {note.content}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotesSection;